<template>
  <div class="carousel-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="carousel-loading">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>
    <!-- 轮播图内容 -->
    <swiper
      v-else-if="slides.length > 0"
      :modules="modules"
      :slides-per-view="1"
      :space-between="0"
      :loop="slides.length > 1"
      :autoplay="{
        delay: 5000,
        disableOnInteraction: false,
      }"
      :pagination="{
        clickable: true,
      }"
      :navigation="true"
      :effect="'fade'"
      :fade-effect="{
        crossFade: true
      }"
      class="mySwiper"
    >
      <swiper-slide 
        v-for="(slide, index) in slides" 
        :key="index"
      >
        <div 
          class="carousel-slide"
          :style="{ backgroundImage: `url(${resolveSwiperImage(slide.image)})` }"
        >
          <!-- <div class="slide-content">
            <h2 class="slide-title" :class="`animate-${slide.animation}`">{{ slide.title }}</h2>
            <p class="slide-description" :class="`animate-${slide.animation}`">{{ slide.description }}</p>
          </div> -->
        </div>
      </swiper-slide>
    </swiper>
    <!-- 错误提示 -->
    <div v-else-if="error" class="carousel-error">
      <p>{{ error }}</p>
      <p class="error-hint">请确保 Node.js 服务器正在运行（端口 3000）</p>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules'
import axios from 'axios'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import 'swiper/css/effect-fade'

// 使用 require.context 加载轮播图图片
const swiperImageCtx = require.context('@/assets/swiper_img', false, /\.(png|jpe?g)$/)

export default {
  name: 'Carousel',
  components: {
    Swiper,
    SwiperSlide
  },
  setup() {
    return {
      modules: [Autoplay, Pagination, Navigation, EffectFade]
    }
  },
  data() {
    return {
      slides: [],
      loading: true,
      error: null
    }
  },
  async mounted() {
    await this.fetchCarouselData()
  },
  methods: {
    async fetchCarouselData() {
      try {
        this.loading = true
        this.error = null
        const response = await axios.get('/api/home/carousel')
        if (response.data.code === 200) {
          this.slides = response.data.data
        } else {
          this.error = response.data.message || '获取轮播图数据失败'
          this.slides = []
        }
      } catch (error) {
        console.error('获取轮播图数据失败:', error)
        this.error = '网络请求失败，请检查服务器是否运行'
        this.slides = []
      } finally {
        this.loading = false
      }
    },
    resolveSwiperImage(imagePath) {
      if (!imagePath) return ''
      if (imagePath.startsWith('@swiper_img/')) {
        const fileName = imagePath.replace('@swiper_img/', '')
        try {
          return swiperImageCtx('./' + fileName)
        } catch (e) {
          console.warn('轮播图图片未找到:', fileName, e)
          return ''
        }
      }
      return imagePath
    }
  }
}
</script>

<style scoped>
@font-face {
  font-family: 'AlimamaDongFangDaKai';
  src: url('@/assets/fonts/text/AlimamaDongFangDaKai-Regular.woff2') format('woff2'),
       url('@/assets/fonts/text/AlimamaDongFangDaKai-Regular.woff') format('woff'),
       url('@/assets/fonts/text/AlimamaDongFangDaKai-Regular.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 5vh;
  min-height: 400px;
  overflow: hidden;
}

/* 加载状态样式 */
.carousel-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: white;
  background: linear-gradient(135deg, #4c95e3 0%, #1e55a2 100%);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.carousel-loading p {
  font-size: 16px;
  margin: 0;
}

/* 错误提示样式 */
.carousel-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ff6b6b;
  background: #f5f5f5;
  padding: 40px;
  text-align: center;
}

.carousel-error p {
  margin: 10px 0;
  font-size: 16px;
}

.error-hint {
  color: #999;
  font-size: 14px;
  margin-top: 10px;
}

.carousel-slide {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.carousel-slide::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* background: rgba(0, 0, 0, 0.3); */
  z-index: 1;
  pointer-events: none; /* 允许点击事件穿透 */
}

.slide-content {
  text-align: center;
  color: white;
  z-index: 2;
  padding: 40px;
  /* background: rgba(0, 0, 0, 0.4); */
  border-radius: 10px;
  backdrop-filter: blur(5px);
  position: relative;
}

.slide-title {
  font-family: 'AlimamaDongFangDaKai', 'Microsoft YaHei', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 62px;
  font-weight: 400;
  letter-spacing: 4px;
  margin-bottom: 20px;
  background: linear-gradient(120deg, #fefefe, #fffdfb);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  /* text-shadow: 0 4px 14px rgba(0, 0, 0, 0.7); */
}

.slide-description {
  font-size: 20px;
  margin-top: 10px;
  /* max-width: 600px; */
}

/* Swiper 基础样式 */
:deep(.swiper) {
  width: 100%;
  height: 100%;
}

/* 导航按钮样式 - 左右切换按钮 */
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  background-color: rgba(128, 128, 128, 0.3);
  color: white;
  width: 20px;
  height: 30px;
  border-radius: 4px;
  transition: all 0.3s ease;
  z-index: 10; /* 确保按钮在最上层 */
  pointer-events: auto; /* 确保按钮可点击 */
  cursor: pointer; /* 显示手型光标 */
  margin-top: -22px; /* 垂直居中 */
}

:deep(.swiper-button-next):after,
:deep(.swiper-button-prev):after {
  font-size: 12px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.8);
}

:deep(.swiper-button-next):hover,
:deep(.swiper-button-prev):hover {
  background-color: rgba(128, 128, 128, 0.5);
  transform: scale(1.05);
}

:deep(.swiper-button-next):hover:after,
:deep(.swiper-button-prev):hover:after {
  color: rgba(255, 255, 255, 1);
}

:deep(.swiper-button-next) {
  right: 20px;
}

:deep(.swiper-button-prev) {
  left: 20px;
}

/* 确保导航按钮在 macOS 上可点击 */
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  -webkit-app-region: no-drag; /* macOS 特定：防止拖拽干扰 */
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation; /* 优化触摸响应 */
}

/* 动画效果 */
.animate-fadeInUp {
  opacity: 0;
  transform: translateY(30px);
  will-change: transform, opacity;
}

.animate-fadeInUp.is-visible {
  animation: fadeInUp 1s ease-out forwards;
}

.animate-slideInRight {
  opacity: 0;
  transform: translateX(50px);
  will-change: transform, opacity;
}

.animate-slideInRight.is-visible {
  animation: slideInRight 1s ease-out forwards;
}

.animate-zoomIn {
  opacity: 0;
  transform: scale(0.8);
  will-change: transform, opacity;
}

.animate-zoomIn.is-visible {
  animation: zoomIn 1s ease-out forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 768px) {
  .carousel-container {
    height: 60vh;
    min-height: 400px;
  }

  .slide-title {
    font-size: 32px;
  }

  .slide-description {
    font-size: 16px;
  }

}
</style>
